import { BaseReplayController } from '@bn-games/history-core';
import { GameState } from '../game/GameState.js';
import type { RuleSet } from '../game/rules/types.js';
import { getRuleSet } from '../game/rules/index.js';
import type { Move } from '../game/types.js';
import type { GameRecord, RecordedMove } from './types.js';

export interface ReplayMoveEvent {
  readonly index: number;
  readonly move: RecordedMove;
  readonly preState: GameState;
  readonly postState: GameState;
}

/**
 * Read-only replay engine over a recorded game. Drives a recomputed
 * `GameState` forward (or seeks by rebuilding from scratch — cheap because
 * `applyMove` is pure and games are < 200 moves). Step index management +
 * seek/backward live in BaseReplayController.
 */
export class ReplayController extends BaseReplayController {
  private readonly record: GameRecord;
  private readonly ruleSet: RuleSet;
  private state: GameState;

  constructor(record: GameRecord) {
    super();
    this.record = record;
    this.ruleSet = getRuleSet(record.variant);
    this.state = GameState.initial(this.ruleSet);
  }

  getRecord(): GameRecord {
    return this.record;
  }

  getState(): GameState {
    return this.state;
  }

  getTotalSteps(): number {
    return this.record.moves.length;
  }

  /** Apply the next recorded move, returning the descriptor so the host can
   * drive the animation. Returns null when already at end. */
  stepForward(): ReplayMoveEvent | null {
    if (this.isAtEnd()) return null;
    const recorded = this.record.moves[this.stepIndex];
    if (!recorded) return null;
    const move = toMove(recorded);
    const preState = this.state;
    const postState = preState.applyMove(move);
    this.state = postState;
    const index = this.stepIndex;
    this.advanceIndex();
    return { index, move: recorded, preState, postState };
  }

  protected reset(): void {
    this.state = GameState.initial(this.ruleSet);
  }

  protected applyStep(index: number): void {
    const rec = this.record.moves[index];
    if (!rec) return;
    this.state = this.state.applyMove(toMove(rec));
  }
}

function toMove(recorded: RecordedMove): Move {
  return {
    from: recorded.from,
    to: recorded.to,
    path: recorded.path,
    captures: recorded.captures,
    promoted: recorded.promoted,
  };
}
